/**
 * Shared ISO-country catalogue for the fee-status profile fields.
 *
 * Order prioritises the countries our 8-country fee engine models
 * (so UK/IE/FR/DE/NL/US/CA/AU surface first in the dropdown), then
 * the remaining EU/EEA + Swiss states, then a long tail of global
 * destinations. Keep names in sync with
 * `backend/services/fee_status.py::_LONG_TO_ISO`.
 *
 * We deliberately store the ISO-2 code as the value — the engine
 * canonicalises either "GB" or the long name, but new writes should
 * always be ISO so downstream filters stay unambiguous.
 */
export const COUNTRIES = [
  // Modelled by the fee engine — pinned to the top.
  { value: "GB", label: "United Kingdom (UK)" },
  { value: "IE", label: "Ireland" },
  { value: "FR", label: "France" },
  { value: "DE", label: "Germany" },
  { value: "NL", label: "Netherlands" },
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "AU", label: "Australia" },
  // Rest of EEA / Switzerland — relevant to fee-status rules.
  { value: "AT", label: "Austria" },
  { value: "BE", label: "Belgium" },
  { value: "BG", label: "Bulgaria" },
  { value: "HR", label: "Croatia" },
  { value: "CY", label: "Cyprus" },
  { value: "CZ", label: "Czech Republic" },
  { value: "DK", label: "Denmark" },
  { value: "EE", label: "Estonia" },
  { value: "FI", label: "Finland" },
  { value: "GR", label: "Greece" },
  { value: "HU", label: "Hungary" },
  { value: "IS", label: "Iceland" },
  { value: "IT", label: "Italy" },
  { value: "LV", label: "Latvia" },
  { value: "LI", label: "Liechtenstein" },
  { value: "LT", label: "Lithuania" },
  { value: "LU", label: "Luxembourg" },
  { value: "MT", label: "Malta" },
  { value: "NO", label: "Norway" },
  { value: "PL", label: "Poland" },
  { value: "PT", label: "Portugal" },
  { value: "RO", label: "Romania" },
  { value: "SK", label: "Slovakia" },
  { value: "SI", label: "Slovenia" },
  { value: "ES", label: "Spain" },
  { value: "SE", label: "Sweden" },
  { value: "CH", label: "Switzerland" },
  // Long-tail — common applicant home countries.
  { value: "NZ", label: "New Zealand" },
  { value: "IN", label: "India" },
  { value: "CN", label: "China" },
  { value: "HK", label: "Hong Kong" },
  { value: "JP", label: "Japan" },
  { value: "KR", label: "South Korea" },
  { value: "SG", label: "Singapore" },
  { value: "MY", label: "Malaysia" },
  { value: "TH", label: "Thailand" },
  { value: "VN", label: "Vietnam" },
  { value: "ID", label: "Indonesia" },
  { value: "PH", label: "Philippines" },
  { value: "AE", label: "United Arab Emirates" },
  { value: "SA", label: "Saudi Arabia" },
  { value: "QA", label: "Qatar" },
  { value: "KW", label: "Kuwait" },
  { value: "OM", label: "Oman" },
  { value: "BH", label: "Bahrain" },
  { value: "JO", label: "Jordan" },
  { value: "LB", label: "Lebanon" },
  { value: "EG", label: "Egypt" },
  { value: "TR", label: "Turkey" },
  { value: "IL", label: "Israel" },
  { value: "NG", label: "Nigeria" },
  { value: "GH", label: "Ghana" },
  { value: "KE", label: "Kenya" },
  { value: "ZA", label: "South Africa" },
  { value: "ET", label: "Ethiopia" },
  { value: "MA", label: "Morocco" },
  { value: "TN", label: "Tunisia" },
  { value: "BR", label: "Brazil" },
  { value: "MX", label: "Mexico" },
  { value: "AR", label: "Argentina" },
  { value: "CL", label: "Chile" },
  { value: "CO", label: "Colombia" },
  { value: "PE", label: "Peru" },
  { value: "RU", label: "Russia" },
  { value: "UA", label: "Ukraine" },
  { value: "PK", label: "Pakistan" },
  { value: "BD", label: "Bangladesh" },
  { value: "LK", label: "Sri Lanka" },
  { value: "NP", label: "Nepal" },
];

/** Map ISO-2 → label for quick render of saved values. */
export const COUNTRY_LABEL = Object.fromEntries(
  COUNTRIES.map((c) => [c.value, c.label]),
);

/** Full label from either an ISO-2 or a stored long form. */
export function countryLabelFromAny(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (COUNTRY_LABEL[s]) return COUNTRY_LABEL[s];
  if (COUNTRY_LABEL[s.toUpperCase()]) return COUNTRY_LABEL[s.toUpperCase()];
  return s;
}

/**
 * Map a free-text country name coming from the DB (uni.country) to the
 * key the fee-status engine uses (UK, IE, FR, DE, NL, US, CA, AU, SG, AE,
 * HK, CN, IN, JP). Anything we don't recognise returns null — callers
 * should fall back to "international" when that happens.
 */
const _NAME_TO_ENGINE_KEY = {
  "united kingdom": "UK", "uk": "UK", "great britain": "UK", "england": "UK",
  "scotland": "UK", "wales": "UK", "northern ireland": "UK", "gb": "UK",
  "ireland": "IE", "ie": "IE", "republic of ireland": "IE",
  "france": "FR", "fr": "FR",
  "germany": "DE", "de": "DE", "deutschland": "DE",
  "netherlands": "NL", "the netherlands": "NL", "nl": "NL", "holland": "NL",
  "united states": "US", "united states of america": "US", "usa": "US", "us": "US",
  "america": "US",
  "canada": "CA", "ca": "CA",
  "australia": "AU", "au": "AU",
  "singapore": "SG", "sg": "SG",
  "united arab emirates": "AE", "uae": "AE", "ae": "AE", "emirates": "AE",
  "hong kong": "HK", "hk": "HK", "hong kong sar": "HK",
  "china": "CN", "cn": "CN", "mainland china": "CN", "people's republic of china": "CN",
  "india": "IN", "in": "IN",
  "japan": "JP", "jp": "JP",
  "finland": "FI", "fi": "FI",
  "italy": "IT", "it": "IT",
  "malaysia": "MY", "my": "MY",
  "taiwan": "TW", "tw": "TW", "republic of china": "TW", "chinese taipei": "TW",
  "türkiye": "TR", "turkiye": "TR", "turkey": "TR", "tr": "TR",
};

export function engineKeyFromCountry(name) {
  if (!name) return null;
  return _NAME_TO_ENGINE_KEY[String(name).trim().toLowerCase()] || null;
}

/**
 * Rough domestic (home) undergraduate tuition in USD per year, used
 * ONLY as a fallback when a degree document doesn't carry its own
 * `tuition_domestic_annual_usd`. Figures match the ranges modelled in
 * `backend/data/country_value.py` and are reviewed quarterly.
 */
export const HOME_FEE_FALLBACK_USD = {
  UK: 12764,   // £10,050 · 2027/28 GOV.UK cap
  IE: 3300,    // €3,000 student contribution
  FR: 320,     // €280 licence
  DE: 630,     // ~€580 semester admin
  NL: 2795,    // €2,530 statutory
  US: 12070,   // avg in-state public
  CA: 5720,    // avg domestic
  AU: 9530,    // avg Commonwealth Supported Place
  SG: 13340,   // avg subsidised MOE Tuition Grant
  AE: 3200,    // public uni Emirati rate
  HK: 5460,    // UGC-funded local
  CN: 890,     // typical domestic
  IN: 1140,    // IIT undergrad for Indian national
  JP: 4060,    // flat ¥535,800 for everyone
  FI: 0,       // EU/EEA tuition-free
  IT: 1800,    // ISEE-based average at public universities
  MY: 2200,    // avg public IPTA Malaysian rate
  TW: 1750,    // avg public uni Taiwanese rate (~NT$55k)
  TR: 150,     // nominal state-uni fee for Turkish citizens
};
